import { useState } from 'react';

interface AppStore {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  totalApps: number;
  activeApps: number;
  inactiveApps: number;
  pendingRequests: number;
  pendingReviews: number;
  activeSubscriptions: number;
  activeTrials: number;
  assignedClients: string[];
}

interface AppStoreManagementProps {
  appStores: AppStore[];
  filter: string;
}

export default function AppStoreManagement({ appStores, filter }: AppStoreManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showManagementPanel, setShowManagementPanel] = useState(false);
  const [selectedStore, setSelectedStore] = useState<AppStore | null>(null);

  // Filter app stores based on the dashboard filter and search
  const filteredAppStores = appStores.filter(store => {
    const matchesSearch = store.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatusFilter = statusFilter === 'All' || store.status === statusFilter;
    const matchesDashboardFilter =
      filter === '' ||
      (filter === 'active' && store.status === 'Active') ||
      (filter === 'inactive' && store.status === 'Inactive');

    return matchesSearch && matchesStatusFilter && matchesDashboardFilter;
  });

  const handleOpenDashboard = (store: AppStore) => {
    setSelectedStore(store);
    setShowManagementPanel(true);
  };

  const handleAddNewStore = () => {
    // Create a new store template
    const newStore: AppStore = {
      id: 'new',
      name: 'New App Store',
      status: 'Active',
      totalApps: 0,
      activeApps: 0,
      inactiveApps: 0,
      pendingRequests: 0,
      pendingReviews: 0,
      activeSubscriptions: 0,
      activeTrials: 0,
      assignedClients: []
    };
    setSelectedStore(newStore);
    setShowManagementPanel(true);
  };

  const toggleStoreStatus = (storeId: string) => {
    alert(`Toggle status for store ID: ${storeId}`);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Search app stores by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* App Stores Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              App Store Management ({filteredAppStores.length} stores)
            </h3>
            <button
              onClick={handleAddNewStore}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Add New Store
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  App Store
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apps Assigned
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned Clients
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Requests
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Reviews
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Subscriptions
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Trials
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAppStores.map((store, index) => (
                <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-store-3-fill text-white text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{store.name}</div>
                        <div className="text-sm text-gray-500">ID: {store.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      store.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        store.status === 'Active' ? 'bg-green-400' : 'bg-red-400'
                      }`}></span>
                      {store.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{store.totalApps}</span> Total
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="text-green-600 font-medium">{store.activeApps}</span> Active,
                      <span className="text-red-600 font-medium ml-1">{store.inactiveApps}</span> Inactive
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{store.assignedClients.length}</span> Clients
                    </div>
                    {store.assignedClients.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        {store.assignedClients.slice(0, 2).join(', ')}
                        {store.assignedClients.length > 2 && ` +${store.assignedClients.length - 2} more`}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      store.pendingRequests > 0
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {store.pendingRequests}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      store.pendingReviews > 0
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {store.pendingReviews}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {store.activeSubscriptions}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {store.activeTrials}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenDashboard(store)}
                        className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium whitespace-nowrap"
                      >
                        <i className="ri-dashboard-line mr-1"></i>
                        Dashboard
                      </button>
                      <button
                        onClick={() => toggleStoreStatus(store.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                          store.status === 'Active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {store.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                        <i className="ri-more-2-line text-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAppStores.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-store-3-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No app stores found</h3>
            <p className="text-gray-600">Try adjusting your search terms or filters</p>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-store-3-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Stores</p>
              <p className="text-2xl font-bold text-gray-900">{appStores.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-circle-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Stores</p>
              <p className="text-2xl font-bold text-gray-900">
                {appStores.filter(store => store.status === 'Active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-apps-line text-2xl text-yellow-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Apps</p>
              <p className="text-2xl font-bold text-gray-900">
                {appStores.reduce((sum, store) => sum + store.totalApps, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(appStores.flatMap(store => store.assignedClients)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* App Store Management Panel */}
      {showManagementPanel && selectedStore && (
        <AppStoreManagementPanel
          store={selectedStore}
          onClose={() => {
            setShowManagementPanel(false);
            setSelectedStore(null);
          }}
        />
      )}
    </div>
  );
}

// App Store Management Panel Component
interface AppStoreManagementPanelProps {
  store: AppStore;
  onClose: () => void;
}

function AppStoreManagementPanel({ store, onClose }: AppStoreManagementPanelProps) {
  const [activeTab, setActiveTab] = useState('apps');

  const tabs = [
    { id: 'apps', name: 'Apps', icon: 'ri-apps-line', count: store.totalApps },
    { id: 'clients', name: 'Clients', icon: 'ri-user-line', count: store.assignedClients.length },
    { id: 'requests', name: 'App Requests', icon: 'ri-file-list-line', count: store.pendingRequests },
    { id: 'reviews', name: 'Reviews', icon: 'ri-star-line', count: store.pendingReviews },
    { id: 'trials', name: 'Trials', icon: 'ri-time-line', count: store.activeTrials },
    { id: 'subscriptions', name: 'Subscriptions', icon: 'ri-calendar-check-line', count: store.activeSubscriptions }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-store-3-fill text-white text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{store.name}</h2>
                <p className="text-gray-600">ID: {store.id} • Status: {store.status}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span>{store.totalApps} Apps</span>
                  <span>{store.assignedClients.length} Clients</span>
                  <span>{store.activeSubscriptions} Subscriptions</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} mr-2 text-lg`}></i>
                {tab.name}
                {tab.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'apps' && <StoreAppsTab store={store} />}
          {activeTab === 'clients' && <StoreClientsTab store={store} />}
          {activeTab === 'requests' && <StoreRequestsTab store={store} />}
          {activeTab === 'reviews' && <StoreReviewsTab store={store} />}
          {activeTab === 'trials' && <StoreTrialsTab store={store} />}
          {activeTab === 'subscriptions' && <StoreSubscriptionsTab store={store} />}
        </div>
      </div>
    </div>
  );
}

// Tab Components
function StoreAppsTab({ store }: { store: AppStore }) {
  const sampleApps = [
    { id: '1', name: 'Municipal Dashboard', status: 'Active', category: 'Governance', installs: 45 },
    { id: '2', name: 'Citizen Portal', status: 'Active', category: 'Citizen Services', installs: 78 },
    { id: '3', name: 'Budget Tracker', status: 'Inactive', category: 'Analytics', installs: 23 }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Apps in {store.name}</h3>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
          <i className="ri-add-line mr-2"></i>
          Add App
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">App Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installs</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleApps.map((app) => (
              <tr key={app.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{app.name}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{app.category}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    app.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {app.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{app.installs}</td>
                <td className="px-6 py-4 text-sm">
                  <button className="text-blue-600 hover:text-blue-800 mr-3">Edit</button>
                  <button className="text-red-600 hover:text-red-800">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreClientsTab({ store }: { store: AppStore }) {
  const sampleClients = [
    { id: '1', name: 'City of Springfield', email: 'admin@springfield.gov', status: 'Active', subscriptions: 5 },
    { id: '2', name: 'Metro Health Dept', email: 'it@metrohealth.org', status: 'Active', subscriptions: 3 },
    { id: '3', name: 'County Services', email: 'tech@county.gov', status: 'Inactive', subscriptions: 0 }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Assigned Clients</h3>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
          <i className="ri-add-line mr-2"></i>
          Assign Client
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscriptions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleClients.map((client) => (
              <tr key={client.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{client.name}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{client.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    client.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {client.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{client.subscriptions}</td>
                <td className="px-6 py-4 text-sm">
                  <button className="text-blue-600 hover:text-blue-800 mr-3">Manage</button>
                  <button className="text-red-600 hover:text-red-800">Unassign</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreRequestsTab({ store }: { store: AppStore }) {
  const sampleRequests = [
    { id: '1', clientName: 'City of Springfield', appName: 'Traffic Management', status: 'Pending', date: '2024-01-15' },
    { id: '2', clientName: 'Metro Health Dept', appName: 'Patient Portal', status: 'Approved', date: '2024-01-14' },
    { id: '3', clientName: 'County Services', appName: 'Document Manager', status: 'Rejected', date: '2024-01-13' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">App Requests</h3>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested App</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleRequests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{request.clientName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{request.appName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{request.date}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'Approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {request.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {request.status === 'Pending' && (
                    <>
                      <button className="text-green-600 hover:text-green-800 mr-3">Approve</button>
                      <button className="text-red-600 hover:text-red-800">Reject</button>
                    </>
                  )}
                  {request.status !== 'Pending' && (
                    <button className="text-blue-600 hover:text-blue-800">View Details</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreReviewsTab({ store }: { store: AppStore }) {
  const sampleReviews = [
    { id: '1', appName: 'Municipal Dashboard', reviewer: 'John Admin', rating: 5, status: 'Pending', date: '2024-01-15' },
    { id: '2', appName: 'Citizen Portal', reviewer: 'Sarah Manager', rating: 4, status: 'Approved', date: '2024-01-14' },
    { id: '3', appName: 'Budget Tracker', reviewer: 'Mike Director', rating: 2, status: 'Rejected', date: '2024-01-13' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Reviews</h3>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">App</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleReviews.map((review) => (
              <tr key={review.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{review.appName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{review.reviewer}</td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-${i < review.rating ? 'fill' : 'line'} text-yellow-400 text-sm`}></i>
                    ))}
                    <span className="ml-2 text-gray-500">{review.rating}/5</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{review.date}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    review.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    review.status === 'Approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {review.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {review.status === 'Pending' && (
                    <>
                      <button className="text-green-600 hover:text-green-800 mr-3">Approve</button>
                      <button className="text-red-600 hover:text-red-800">Reject</button>
                    </>
                  )}
                  {review.status !== 'Pending' && (
                    <button className="text-blue-600 hover:text-blue-800">View</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreTrialsTab({ store }: { store: AppStore }) {
  const sampleTrials = [
    { id: '1', appName: 'Municipal Dashboard', clientName: 'City of Springfield', startDate: '2024-01-01', endDate: '2024-01-31', status: 'Active' },
    { id: '2', appName: 'Citizen Portal', clientName: 'Metro Health Dept', startDate: '2023-12-15', endDate: '2024-01-14', status: 'Expired' },
    { id: '3', appName: 'Budget Tracker', clientName: 'County Services', startDate: '2024-01-10', endDate: '2024-02-09', status: 'Active' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Trial Subscriptions</h3>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">App</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleTrials.map((trial) => (
              <tr key={trial.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{trial.appName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{trial.clientName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{trial.startDate}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{trial.endDate}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trial.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {trial.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  <button className="text-blue-600 hover:text-blue-800 mr-3">Extend</button>
                  <button className="text-red-600 hover:text-red-800">Cancel</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreSubscriptionsTab({ store }: { store: AppStore }) {
  const sampleSubscriptions = [
    { id: '1', appName: 'Municipal Dashboard', clientName: 'City of Springfield', plan: 'Premium', price: '$99/month', status: 'Active', nextBilling: '2024-02-01' },
    { id: '2', appName: 'Citizen Portal', clientName: 'Metro Health Dept', plan: 'Basic', price: '$49/month', status: 'Active', nextBilling: '2024-01-25' },
    { id: '3', appName: 'Budget Tracker', clientName: 'County Services', plan: 'Enterprise', price: '$199/month', status: 'Cancelled', nextBilling: '-' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Active Subscriptions</h3>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">App</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Billing</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sampleSubscriptions.map((subscription) => (
              <tr key={subscription.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{subscription.appName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{subscription.clientName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{subscription.plan}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{subscription.price}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    subscription.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {subscription.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{subscription.nextBilling}</td>
                <td className="px-6 py-4 text-sm">
                  {subscription.status === 'Active' && (
                    <>
                      <button className="text-blue-600 hover:text-blue-800 mr-3">Manage</button>
                      <button className="text-red-600 hover:text-red-800">Cancel</button>
                    </>
                  )}
                  {subscription.status === 'Cancelled' && (
                    <button className="text-green-600 hover:text-green-800">Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
